import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../foundations/Icon';
import css from './AdvancedAutoCompleteInput.css';
import Tag from '../../atoms/Tag';
import Tooltip from '../Tooltip';
import { AutoCompleteOptionType } from '../autocompleteTypes';
import OverflowingText from '../../foundations/OverflowingText';

const SEARCH_PLACEHOLDER = 'STATIC.AUTOCOMPLETE.SEARCH_PLACEHOLDER';
const SHOW_ALL_TAGS = 'STATIC.AUTOCOMPLETE.SHOW_ALL_TAGS';
const HIDE_TAGS = 'STATIC.AUTOCOMPLETE.HIDE_TAGS';

const RemoveTagIcon = React.memo(({ onClick, disabled }) => (
    <Icon
        name="close-line"
        type={IconTypes.MINGCUTE}
        className={css.removeTagIcon}
        onClick={onClick}
        variant={IconVariants.ACTIVE}
        disabled={disabled}
    />
));

RemoveTagIcon.propTypes = {
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
};

const RemoveIcon = React.memo(({ onClick, disabled }) => (
    <Icon
        type={IconTypes.MINGCUTE}
        name="close-circle-fill"
        size={IconSizes.MEDIUM}
        variant={IconVariants.LIGHT}
        onClick={onClick}
        className={css.removeIcon}
        disabled={disabled}
    />
));

RemoveIcon.propTypes = {
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
};

const AdvancedAutoCompleteInput = ({
    isOpen,
    loading,
    removable,
    onRemove,
    onFieldClick,
    setSearch,
    values,
    tagsLimit,
    disabled,
    onChange,
    translate,
    isMulti,
    isEmptyWarning,
    clearable = true,
}) => {
    const inputRef = useRef();
    const [showAllTags, setShowAllTags] = useState(false);

    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setShowAllTags(false);
        }
    }, [isOpen]);

    const removeTag = (event, removedValue) => {
        event.stopPropagation();
        onChange(values.filter(item => item.value !== removedValue));
    };

    const handleToggleAllTags = event => {
        event.stopPropagation();
        setShowAllTags(prev => !prev);
    };

    const handleRemainingTagClick = event => {
        event.stopPropagation();
        onFieldClick(event);
        setShowAllTags(true);
    };

    const displayTags = showAllTags ? values : values.slice(0, tagsLimit);
    const remainingCount = values.length - tagsLimit;
    const hasHiddenTags = !showAllTags && remainingCount > 0;

    const handleInputChange = event => {
        event.stopPropagation();
        setSearch(event.target.value);
    };

    const handleInputClick = event => {
        event.stopPropagation();
    };

    const onSingleValueClear = event => {
        event.stopPropagation();
        onChange(null);
    };

    const remainingNumberText = `+${remainingCount}`;

    const selectedSingleValue = !isMulti ? values[0] : null;

    return (
        <div
            className={classNames(css.inputWrapper, {
                [css.disabled]: disabled || loading,
                [css.disabledView]: disabled,
                [css.inputWrapperOpen]: isOpen,
                [css.inputWrapperOpenValues]: isOpen && values.length && isMulti,
                [css.emptyWarning]: isEmptyWarning,
            })}
        >
            <div className={css.inputBox} onClick={onFieldClick}>
                <div className={css.endAdornment}>
                    {loading && <CircularProgress color="primary" size={16} />}
                    <Icon
                        name={isOpen ? 'up-fill' : 'down-fill'}
                        type={IconTypes.MINGCUTE}
                        size={IconSizes.SMALL}
                        variant={IconVariants.LIGHT}
                    />
                </div>
                <div className={css.content}>
                    {isOpen ? (
                        <>
                            <div className={css.searchContainer}>
                                <Icon
                                    name="search-line"
                                    size={IconSizes.SMALL}
                                    variant={IconVariants.LIGHT}
                                    className={css.searchIcon}
                                />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    autoComplete="off"
                                    className={css.searchInput}
                                    placeholder={translate(SEARCH_PLACEHOLDER)}
                                    onChange={handleInputChange}
                                    onClick={handleInputClick}
                                />
                            </div>
                            {isMulti && !!values.length && <div className={css.divider} />}
                        </>
                    ) : (
                        !!selectedSingleValue && (
                            <div className={css.singleValueContainer}>
                                <div className={css.selectedValue}>
                                    {selectedSingleValue.icon}
                                    <OverflowingText>{translate(selectedSingleValue.label)}</OverflowingText>
                                </div>
                                {clearable && <RemoveIcon onClick={onSingleValueClear} disabled={disabled} />}
                            </div>
                        )
                    )}
                    {isMulti && !!values.length && (
                        <div
                            className={classNames(css.tags, {
                                [css.tagsExpanded]: showAllTags && values.length > tagsLimit,
                            })}
                        >
                            {displayTags.map(({ value, label, icon }) => (
                                <Tag
                                    isFadingBackground={false}
                                    key={value}
                                    color="var(--blue100)"
                                    className={classNames(css.tag, {
                                        [css.smallTag]: displayTags.length > 1,
                                        [css.disabledTag]: disabled,
                                    })}
                                    value={label}
                                    leftIcon={icon}
                                    rightIcon={
                                        <RemoveTagIcon onClick={event => removeTag(event, value)} disabled={disabled} />
                                    }
                                />
                            ))}
                            {isOpen && remainingCount > 0 && (
                                <Tag
                                    value={
                                        showAllTags
                                            ? translate(HIDE_TAGS)
                                            : `${translate(SHOW_ALL_TAGS)} ${remainingNumberText}`
                                    }
                                    color="var(--gray100)"
                                    isFadingBackground={false}
                                    onClick={handleToggleAllTags}
                                />
                            )}
                            {!isOpen && hasHiddenTags && (
                                <Tag
                                    value={`${remainingNumberText}`}
                                    color="var(--gray100)"
                                    isFadingBackground={false}
                                    onClick={handleRemainingTagClick}
                                    className={classNames(css.remainingCountTag, { [css.disabledTag]: disabled })}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
            {removable && (
                <>
                    <div className={css.indicatorSeparator} />
                    <Tooltip
                        wrapperClass={css.tooltipContainer}
                        titleTranslationKey="STATIC.AUTOCOMPLETE.REMOVE_TOOLTIP"
                    >
                        <RemoveIcon onClick={onRemove} disabled={disabled} />
                    </Tooltip>
                </>
            )}
        </div>
    );
};

AdvancedAutoCompleteInput.propTypes = {
    removable: PropTypes.bool,
    onRemove: PropTypes.func,
    onFieldClick: PropTypes.func,
    setSearch: PropTypes.func,
    values: PropTypes.arrayOf(AutoCompleteOptionType),
    tagsLimit: PropTypes.number,
    disabled: PropTypes.bool,
    isOpen: PropTypes.bool,
    loading: PropTypes.bool,
    onChange: PropTypes.func,
    translate: PropTypes.func,
    isMulti: PropTypes.bool,
    isEmptyWarning: PropTypes.bool,
    clearable: PropTypes.bool,
};

export default withLocalize(AdvancedAutoCompleteInput);
